import { useState, useEffect, useRef, FC, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { useQueryParams } from "src/hooks/useQueryParams";
import { Dropdown } from "src/components/Dropdown";
import { ISelectOption } from "src/@types/form";
import { SearchOption } from "./SearchOption";
import { QueryParamKeys } from "./types";
import "./scrollbar.scss";

interface Props {
  className?: string;
  icon: FC;
  title: string;
  queryParamKey: QueryParamKeys;
  options: ISelectOption[];
}

export const SearchDropdownList: FC<Props> = ({
  className,
  icon: Icon,
  title,
  queryParamKey,
  options,
}) => {
  const { t } = useTranslation();

  const { getQueryParam, setMultipleQueryParams } = useQueryParams();

  const activeOption = options.find(
    ({ value }) => value === getQueryParam(queryParamKey)
  );

  const setOption = ({ value }: ISelectOption) => {
    setMultipleQueryParams({
      [queryParamKey]: value,
    });
  };

  const activeLabel = activeOption
    ? typeof activeOption.label !== "string"
      ? t(activeOption.label.i18nKey)
      : activeOption.label
    : t(title);

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);

  const handleQuery = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const headDropdownLabel = (
    <>
      <div>
        <Icon />
      </div>
      {isOpen ? (
        <input
          className="bg-inherit w-full gl:text-xl border-b border-purple-base"
          value={query}
          onChange={handleQuery}
          ref={inputRef}
        />
      ) : (
        <span className="whitespace-nowrap gl:text-xl overflow-hidden overflow-ellipsis border-b border-b-transparent">
          {activeLabel}
        </span>
      )}
    </>
  );

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }

    return () => {
      setQuery("");
    };
  }, [isOpen]);

  // виправити (label as string) можна додати пошук і по value
  const filteredOptions = options.filter(
    ({ label }) =>
      label !== activeLabel &&
      (label as string).toLowerCase().includes(query.toLowerCase())
  );

  const setActiveOption = (option: ISelectOption) => {
    setOption(option);
    setIsOpen(false);
  };

  return (
    <Dropdown
      className={cn("!static", className)}
      head={headDropdownLabel}
      headClassName="flex !justify-start gap-2 w-full text-purple-base"
      bodyClassName="!left-0 !right-0 lg:top-15 !-translate-x-0 z-10 max-h-60 bg-blue-dark-10 backdrop-blur px-5 shadow-purple-base-20 scrollbar overflow-auto rounded-20"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      isActiveIndicatorArrow={false}
    >
      <ul>
        {filteredOptions.map((option) => (
          <SearchOption
            key={option.id}
            option={option}
            setOption={setActiveOption}
          />
        ))}
      </ul>
    </Dropdown>
  );
};
